/**
 * Testimonial module
 *
 * Blockquote with source and a rounded image
 */

// Deps
import { createComponent, FelaComponent } from "react-fela";
import { gutters, colors, layout } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";
import { ContentMaxWidth } from "~/components/layout/max-width";

import { makeAutoplaySource, stripSecurityAttributes } from "~/modules/video";
import DeferredEmbed from "~/components/shared/deferred-embed";

// Components
import Visual from "~/components/shared/visual";
import WordpressLink from "~/components/global/links";

export default function TestimonialBlock({ block, isLcpCandidate }) {
    const {
        quote,
        source,
        source_title,
        image,
        image_side,
        logo,
        cta_link,
        cta_label = "Learn More",
        media_type = "image",
        large_image,
        video_embed,
        anchor_id
    } = block;

    const CTA = isMobile => {
        return (
            <>
                {cta_link && cta_label && (
                    <WordpressLink
                        label={cta_label}
                        url={cta_link}
                        cta_arrow="true"
                        extend={combineRules(
                            textStyles.paragraphLarge,
                            ctaStyle(isMobile),
                        )}
                    />
                )}
            </>
        );
    };


      return (
        <Container id={anchor_id} className="module_testimonial_block module-testimonial-block">

            <ContentMaxWidth extend={colWrapStyles({ imageSide: image_side })}>
                <Col imageSide={image_side}>
                    <Visual image={logo} extend={logoStyle} />
                    {/* Quote */}
                    {quote && <blockquote dangerouslySetInnerHTML={{ __html: quote }} />}
                    {/* Source */}
                    {source && (
                        <FelaComponent as="div" style={sourceStyle}>
                            {image && (
                                <Visual
                                    image={image}
                                    extend={photoStyle}
                                />
                            )}
                            <FelaComponent as="div">
                                {source} is the
                                {source_title && <span>{source_title}</span>}
                            </FelaComponent>
                        </FelaComponent>
                    )}
                    {CTA(true)}
                    {CTA(false)}
                </Col>
                <Col imageSide={image_side}>
                    {media_type === "image" && (
                        <Visual
                            autoplay
                            playsinline
                            muted
                            loop
                            image={large_image}
                            extend={imageStyles}
                        />
                    )}
                    {media_type === "wistia" && (
                        <Content
                            extend={videoPlayerStyles}
                        >
                            <DeferredEmbed
                                className="video-player"
                                immediate={isLcpCandidate}
                                html={stripSecurityAttributes(
                                    makeAutoplaySource(video_embed)
                                )}
                            />
                        </Content>
                    )}
                </Col>
            </ContentMaxWidth>
        </Container>
    );
}

const videoPlayerStyles = {
    width: "100%"
};

const logoStyle = {
    maxWidth: 125,
    height: "auto",
    marginBottom: 20,
    "& .vv-visual *": {
        height: "auto"
    },
    maxWidthMobileLandscape: {
        marginLeft: "auto",
        marginRight: "auto"
    }
};

const ctaStyle = isMobile => ({
    marginTop: 40,
    display: "block",
    color: colors.blue,
    extend: [
        {
            condition: isMobile,
            style: {
                minWidthMobileLandscape: {
                    display: "none",
                    height: 0,
                    overflow: "hidden",
                    marginLeft: "auto",
                    marginRight: "auto"
                }
            }
        },
        {
            condition: !isMobile,
            style: {
                maxWidthMobileLandscape: {
                    display: "none",
                    height: 0,
                    overflow: "hidden"
                }
            }
        }
    ]
});

const photoStyle = {
    display: "block",
    marginRight: 12,
    height: 90,
    width: 90,
    borderRadius: "50%",
    overflow: "hidden",
    maxWidthMobile: {
        height: 30,
        width: 45
    },
    "& .vv-align-middle": {
        display: "none"
    },
    "& .vv-visual": {
        height: "100%",
        width: "100%"
    },
    "& .vv-visual *": {
        height: "auto"
    }
};

const sourceStyle = {
  fontSize: "18px",
    marginTop: 24,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    width: "100%",
    "& span": {
        display: "block"
    }
};

const Container = createComponent(
    () => ({
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        minHeight: 520,
        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["id"]
);

const Content = createComponent(({ imageSide }) => ({
    width: "50%",
    zIndex: 5,
    marginRight: 30,
    maxWidthMobileLandscape: {
        width: "100%",
        marginRight: 0,
        textAlign: "center"
    },
    "> blockquote": {
        ...textStyles.blockquote
    },

    extend: [
        {
            condition: imageSide === "right",
            style: {
                width: "calc(50% - 40px)",
                marginRight: 0
            }
        }
    ]
}));

const colWrapStyles = ({ imageSide }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    maxWidth: 1300,
    position: "relative",
    maxWidthMobile: {
        flexWrap: "wrap"
    },

    extend: [
        {
            condition: imageSide === "left",
            style: {
                flexDirection: "row-reverse"
            }
        }
    ]
});

const colMargin = direction =>
    fluid(direction, {
        min: 20,
        max: 40,
        maxWidth: layout.contentMaxWidth
    });

const Col = createComponent(({ imageSide }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    flexWrap: "wrap",

    "> blockquote": {
        width: "100%",
        ...textStyles.blockquote
    },

    ":first-of-type": {
        width: "55%"
    },
    ":last-of-type": {
        width: "45%"
    },

    minWidthMobile: {
    },

    maxWidthMobile: {
        width: "100%!important",
        textAlign: "center",
        ":first-of-type": {
            marginBottom: 44
        }
    },

    extend: [
        {
            condition: imageSide == "right",
            style: {
                minWidthTablet: {
                    ":first-of-type": {
                        extend: [colMargin("marginRight")]
                    }
                },

                minWidthMobile: {
                    ":last-of-type": {
                        extend: [colMargin("marginLeft")],
                        alignItems: "stretch"
                    }
                }
            }
        },
        {
            condition: imageSide == "left",
            style: {
                minWidthTablet: {
                    ":first-of-type": {
                        extend: [colMargin("marginLeft")]
                    }
                },
                minWidthMobile: {
                    ":last-of-type": {
                        extend: [colMargin("marginRight")],
                        alignItems: "stretch"
                    }
                }
            }
        }
    ]
}));
const imageStyles = {
    width: "100%",
    "& .vv-visual": {
        width: "100%",
        "& *": {
            width: "100%"
        }
    },
    "& .vv-video": {
        width: "100%",
        "& *": {
            width: "100%"
        }
    }
};
